import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { getData } from "../../Webservice/Webservice";

// Cormorant-style font from old project
const corfont = {
  fontFamily: "'Cormorant', Georgia, serif",
  letterSpacing: "0.4px",
};

// Proper responsive settings
const responsive = {
  superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 1 },
  desktop: { breakpoint: { max: 2999, min: 1024 }, items: 1 },
  tablet: { breakpoint: { max: 1023, min: 464 }, items: 1 },
  mobile: { breakpoint: { max: 463, min: 0 }, items: 1 },
};



export default function HomeBanner() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await getData("homepage/banner/list", null, token);

        if (response && response.banner) {
          // Filter out inactive banners and sort by position
          const activeBanners = response.banner
            .filter((banner) => banner.status === "active")
            .sort((a, b) => a.position - b.position);
          setBanners(activeBanners);
        } else {
          setError("No banners available");
        }
      } catch (err) {
        console.error("Error fetching banners:", err);
        setError("Failed to load banners. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" p={3} color="error.main">
        {error}
      </Box>
    );
  }

  if (banners.length === 0) {
    return (
      <Box textAlign="center" p={3} color="text.secondary">
        No banners available
      </Box>
    );
  }

  return (
    <Box

      sx={{
        margin: 0,
        width: "100%",
        position: "relative",
        overflow: "hidden",
        height: { xs: "40vh", sm: "40vh", md: "85vh", lg: "92vh" }
      }}
    >
      <Carousel
        responsive={responsive}
        infinite
        autoPlay
        autoPlaySpeed={3000}
        transitionDuration={800}
        removeArrowOnDeviceType={["tablet", "mobile", "desktop"]}
        showDots={banners.length > 1}
        pauseOnHover
        swipeable
        draggable
        customDot={<CustomDot />}
        dotListClass="custom-dot-list"
        containerClass="carousel-container"
        itemClass="carousel-item"
      >
        {banners.map((banner, index) => (
          <Box
            key={banner._id || index}
            sx={{
              width: "100%",
              height: { xs: "40vh", sm: "40vh", md: "85vh", lg: "92vh" },
              position: "relative",
              cursor: "pointer",
            }}
            onClick={() => {
              if (banner.externalUrl) {
                window.open(banner.externalUrl, "_blank");
              } else if (banner.type === "product" && banner.link) {
                navigate(`/product/${banner.link}`);
              } else if (banner.type === "subcategory" && banner.link) {
                navigate("/search", {
                  state: {
                    subcategoryId: banner.link,
                    title: "Subcategory Products"
                  }
                });
              }
            }}
          >
            <img
              src={banner.image}
              alt={`Banner ${index + 1}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/img/placeholder-banner.jpg";
              }}
              draggable={false}
            />
          </Box>
        ))}
      </Carousel>



      {/* Custom Dot Styling */}
      <style jsx global>{`
        .custom-dot-list {
          bottom: 20px !important;
          text-align: center;
        }
        .react-multi-carousel-dot button {
          width: 10px !important;
          height: 10px !important;
          border-radius: 50% !important;
          border: none !important;
          background: rgba(255, 255, 255, 0.5) !important;
          transition: all 0.3s !important;
        }
        .react-multi-carousel-dot--active button {
          background: white !important;
          transform: scale(1.2);
        }
        .carousel-item {
          padding: 0 !important;
        }
      `}</style>
    </Box>
  );
}

// Custom dot component
const CustomDot = ({ onClick, active }) => {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? "white" : "rgba(255,255,255,0.5)",
        border: "none",
        margin: "0 5px",
        cursor: "pointer",
        transition: "all 0.3s",
        transform: active ? "scale(1.2)" : "scale(1)",
      }}
    />
  );
};
